import { useTranslation } from 'react-i18next'
import { Clock, Timer } from 'lucide-react'
import { Button } from '@ascleo/ui/components/ui/button'
import { Small } from '../../../components/typography/small'
import { useSizing } from '../../../lib/use-sizing'

interface TimedTerminalFooterProps {
  /** Current countdown in seconds */
  countdown: number
  /** Whether the patient has already extended */
  hasExtended: boolean
  /** Callback to extend the timer */
  onExtendTime: () => void
}

/**
 * Shared countdown + "more time" UI for timed terminal screens.
 *
 * Displays the remaining time, explains what happens when it expires,
 * and offers a "more time" button.
 *
 * @since 1.0.0
 */
export function TimedTerminalFooter({
  countdown,
  hasExtended,
  onExtendTime,
}: TimedTerminalFooterProps) {
  const { t } = useTranslation()
  const sizing = useSizing()

  return (
    <output
      data-testid="frontdesk-timed-terminal-footer"
      aria-live="polite"
      className="flex flex-col items-center gap-3 mt-4"
    >
      {/* Countdown display */}
      <span className="flex items-center gap-3 px-6 py-3 bg-muted/50 rounded-lg">
        <Clock className="size-5 text-muted-foreground" />
        <Small as="span" size={sizing.small} intent="muted">
          {t('self-service.timed-terminal.countdown', { seconds: countdown })}
        </Small>
      </span>

      {/* What happens when time expires */}
      <Small
        as="span"
        data-testid="frontdesk-timed-terminal-outcome"
        size={sizing.small}
        intent="muted"
      >
        {t('self-service.timed-terminal.countdown-outcome')}
      </Small>

      {/* More time button */}
      <Button
        data-testid="frontdesk-timed-terminal-more-time-btn"
        type="button"
        variant="outline"
        size={sizing.buttonSecondary}
        onClick={onExtendTime}
      >
        <Timer className="size-5 mr-2" />
        {hasExtended
          ? t('self-service.timed-terminal.extended')
          : t('self-service.timed-terminal.more-time')}
      </Button>
    </output>
  )
}
